import { RemoveRedEyeTwoTone } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from 'src/components/Footer';
import { useAppSelector } from '../../../redux/root';
import {
  AlQuran,
  Azkaar,
  Countable,
  Filter,
  Khassida,
  UserInfos
} from '../../../types';
import { useDashboardHome, useDashboardUser } from '../../../utils/api/hooks';
import {
  deleteUserFunction,
  toggleUserStatus
} from '../../../utils/api/user.api';
import { useDashboard } from '../../../utils/data';
import { Select } from '../../common/Select';
import Table, {
  DeleteButton,
  HeadCell,
  SwitchButton,
  UserFormatter
} from '../../common/Table';
import { ShowUserButton } from '../user/UserTable';
import { BookCard, CountCard, UserCard } from './Wallets';
import AlQuranModal from './modals/AlQuranModal';
import AzkaarModal from './modals/AzkaarModal';
import KhassidaModal, {
  CountableMemberWithCount,
  LimtuModal
} from './modals/KhassidaModal';
import SalatouModal from './modals/SalatouModal';

export type FilterItemValue<T> = { value: T; title: string };

export const FILTERS: FilterItemValue<Filter>[] = [
  { title: 'Cette semaine', value: 'week' },
  { title: 'Semaine passée', value: 'last_week' },
  { title: "Ajourd'hui", value: 'today' },
  { title: 'Ce mois-ci', value: 'month' },
  { title: 'Le mois passé', value: 'last_month' },
  { title: 'Date', value: 'date_between' }
];

export const FILTERS_USERS: FilterItemValue<number>[] = [
  { title: 'Membres à jour', value: 0 },
  { title: 'Membres en retard', value: 1 }
];

export function useFilterMembers() {
  const [filterUser, setFilterUser] = useState(0);
  const renderFilterMembers = useCallback(() => {
    return (
      <Tabs
        onChange={(e, value) => {
          setFilterUser(value);
        }}
        sx={{ mb: 1 }}
        value={filterUser}
        variant="scrollable"
        scrollButtons="auto"
        textColor="primary"
        indicatorColor="primary"
      >
        {FILTERS_USERS.map((tab) => (
          <Tab
            style={{ fontSize: 11, textTransform: 'none' }}
            key={tab.value}
            label={tab.title}
          />
        ))}
      </Tabs>
    );
  }, [filterUser]);

  return {
    renderFilterMembers,
    filterUser
  };
}

export function useDateFilter(all?: boolean, withCampagne?: boolean) {
  const [active, setActive] = useState(0);
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(
    moment().add(1, 'days').format('YYYY-MM-DD')
  );

  const campagnes = useAppSelector((s) => s.campanges);
  const [campagne, setCampagne] = useState(
    campagnes.find((c) => c.active)?.uid ?? '0000'
  );

  useEffect(() => {
    const finded = campagnes.find((c) => c.active);
    if (finded) {
      setCampagne(finded.uid);
    }
  }, [campagnes]);

  const render = useCallback(() => {
    return (
      <>
        <Grid sx={{ mb: 2 }} item sm={12} md={12}>
          <Stack direction={'row'} alignItems={'center'} spacing={2}>
            {withCampagne && (
              <Select
                value={campagne}
                onChange={(e) => {
                  setCampagne(e.target.value);
                }}
                margin="none"
                placeholder={'Type'}
                variant="outlined"
                data={[
                  { label: 'Campagne', value: '0000' },
                  ...campagnes.map((c) => ({ label: c.titre, value: c.uid }))
                ]}
              />
            )}
            <Tabs
              onChange={(e, value) => {
                setActive(value);
              }}
              value={active}
              variant="scrollable"
              scrollButtons="auto"
              textColor="primary"
              indicatorColor="primary"
            >
              {FILTERS.map((tab, index) => (
                <Tab style={{ fontSize: 11 }} key={index} label={tab.title} />
              ))}
              {all && <Tab style={{ fontSize: 11 }} label={'Tous'} />}
            </Tabs>
          </Stack>
        </Grid>
        {active === 5 && (
          <Grid
            alignItems={'center'}
            sx={{ my: 1 }}
            container
            item
            md={12}
            sm={12}
          >
            <Grid item md={1} sm={12}>
              <Typography textAlign={'center'}>Du</Typography>
            </Grid>
            <Grid item md={3} sm={12}>
              <TextField
                type="date"
                margin="none"
                required
                fullWidth
                value={startDate}
                onChange={(e) => {
                  setStartDate(moment(e.target.value).format('YYYY-MM-DD'));
                }}
                variant="outlined"
              />
            </Grid>
            <Grid item md={1} sm={12}>
              <Typography textAlign={'center'}>Au</Typography>
            </Grid>
            <Grid item md={3} sm={12}>
              <TextField
                type="date"
                margin="none"
                required
                fullWidth
                value={endDate}
                onChange={(e) => {
                  setEndDate(moment(e.target.value).format('YYYY-MM-DD'));
                }}
                variant="outlined"
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  }, [active, startDate, endDate, campagne]);

  return {
    render,
    active,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    campagne: campagne === '0000' ? undefined : campagne,
    campagnes
  };
}

function DashboardAdmin() {
  const navigate = useNavigate();
  const dataCount = useDashboard();
  const { filterUser, renderFilterMembers } = useFilterMembers();
  const [showKhassida, setShowKhassida] = useState(false);
  const [showAlQuran, setShowAlQuran] = useState(false);
  const [showAzkaar, setShowAzkaar] = useState(false);
  const [showSalatou, setShowSalatou] = useState(false);

  const { active, startDate, endDate, render, campagne } = useDateFilter(
    false,
    true
  );
  const filter = FILTERS[active].value;
  const { dashboard, data, loading } = useDashboardHome(
    filter,
    startDate,
    endDate,
    campagne
  );

  return (
    <>
      <Helmet>
        <title>Dashboard</title>
      </Helmet>
      {showSalatou && (
        <SalatouModal
          show={showSalatou}
          onHide={() => {
            setShowSalatou(false);
          }}
          data={data}
        />
      )}
      {showKhassida && (
        <KhassidaModal
          show={showKhassida}
          onHide={() => {
            setShowKhassida(false);
          }}
          data={data}
        />
      )}
      {showAlQuran && (
        <AlQuranModal
          show={showAlQuran}
          onHide={() => {
            setShowAlQuran(false);
          }}
          data={data}
        />
      )}
      {showAzkaar && (
        <AzkaarModal
          show={showAzkaar}
          onHide={() => {
            setShowAzkaar(false);
          }}
          data={data}
        />
      )}
      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          sx={{ mt: 1 }}
          spacing={1}
        >
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <BookCard
                onClick={() => {
                  navigate('/admin/khassida');
                }}
                count={dataCount.khassida}
                title={'Liste xassiida'}
              />
              <BookCard
                onClick={() => {
                  navigate('/admin/al-quran');
                }}
                count={dataCount.alquran}
                title={'Liste al quran'}
              />
              <BookCard
                onClick={() => {
                  navigate('/admin/azkaar');
                }}
                count={dataCount.azkaar}
                title={'Liste azkaar'}
              />
              <UserCard
                onClick={() => {
                  navigate('/admin/utilisateurs');
                }}
                count={dataCount.users}
                title={'Liste utilisateurs'}
              />
              <Grid sx={{ my: 1 }} item sm={12} md={12}>
                <Divider />
              </Grid>
              {render()}
              <CountCard
                loading={loading}
                count={dashboard.salatou}
                title={'Limu Salaatu Yi'}
                onClick={() => setShowSalatou(true)}
              />
              <CountCard
                count={dashboard.khassida}
                loading={loading}
                onClick={() => setShowKhassida(true)}
                title={'Limu  Xasiida yi'}
              />
              <CountCard
                onClick={() => setShowAlQuran(true)}
                count={dashboard.alQuran}
                loading={loading}
                title={'Limu al quran ji'}
              />
              <CountCard
                onClick={() => setShowAzkaar(true)}
                count={dashboard.azkaar}
                loading={loading}
                title={'Limu Azkaar yi'}
              />
              <Grid item xs={12} md={12}>
                {renderFilterMembers()}
                <UsersCountTable filter={filterUser} data={data} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </>
  );
}

export function DashboardUser({ item }: { item: UserInfos }) {
  const [showSalatou, setShowSalatou] = useState(false);
  const [showKhassida, setShowKhassida] = useState(false);
  const [showAlQuran, setShowAlQuran] = useState(false);
  const [showAzkaar, setShowAzkaar] = useState(false);
  const { active, startDate, endDate, render, campagne } = useDateFilter(
    false,
    true
  );
  const filter = FILTERS[active].value;
  const { dashboard, data } = useDashboardUser(
    filter,
    item.uid,
    startDate,
    endDate,
    campagne
  );
  return (
    <>
      {showSalatou && (
        <SalatouModal
          show={showSalatou}
          onHide={() => {
            setShowSalatou(false);
          }}
          data={data}
        />
      )}
      {showKhassida && (
        <KhassidaModal
          show={showKhassida}
          onHide={() => {
            setShowKhassida(false);
          }}
          data={data}
        />
      )}
      {showAlQuran && (
        <AlQuranModal
          show={showAlQuran}
          onHide={() => {
            setShowAlQuran(false);
          }}
          data={data}
        />
      )}
      {showAzkaar && (
        <AzkaarModal
          show={showAzkaar}
          onHide={() => {
            setShowAzkaar(false);
          }}
          data={data}
        />
      )}

      <Grid container spacing={1}>
        {render()}
        <CountCard
          count={dashboard.salatou}
          title={'Limu Salaatu Yi'}
          onClick={() => setShowSalatou(true)}
        />
        <CountCard
          count={dashboard.khassida}
          onClick={() => setShowKhassida(true)}
          title={'Limu  Xasiida yi'}
        />
        <CountCard
          onClick={() => setShowAlQuran(true)}
          count={dashboard.alQuran}
          title={'Limu al quran ji'}
        />
        <CountCard
          onClick={() => setShowAzkaar(true)}
          count={dashboard.azkaar}
          title={'Limu Azkaar yi'}
        />
      </Grid>
    </>
  );
}

export default DashboardAdmin;

type UserCount = {
  uid: string;
  user: UserInfos;
  date: number;
  salatou: number;
  khassida: number;
  alquran: number;
  azkaar: number;
  count: number;
  dataSalatou: Countable[];
  dataKhassida: Countable[];
  dataAlquran: Countable[];
  dataAzkaar: Countable[];
};

export function useUsersCount(data: Countable[], filter: number) {
  const [items, setItems] = useState<UserCount[]>([]);
  const users = useAppSelector((s) => s.admins);
  useEffect(() => {
    const obj: { [k: string]: UserCount } = {};
    for (let u of users) {
      obj[u.uid] = {
        uid: u.uid,
        date: 0,
        user: u,
        alquran: 0,
        khassida: 0,
        salatou: 0,
        azkaar: 0,
        count: 0,
        dataAlquran: [],
        dataKhassida: [],
        dataSalatou: [],
        dataAzkaar: []
      };
    }

    for (let i of data) {
      if (obj[i.user]) {
        obj[i.user].count += i.count;
        if (i.salatou) {
          obj[i.user].salatou += i.count;
          obj[i.user].dataSalatou.push(i);
        } else if (i.khassida) {
          obj[i.user].khassida += i.count;
          obj[i.user].dataKhassida.push(i);
        } else if (i.azkaar) {
          obj[i.user].azkaar += i.count;
          obj[i.user].dataAzkaar.push(i);
        } else if (i.alQuran) {
          obj[i.user].alquran += i.count;
          obj[i.user].dataAlquran.push(i);
        }
        if (i.date.date > obj[i.user].date) {
          obj[i.user].date = i.date.date;
        }
      }
    }
    if (filter === 0) {
      setItems(
        Object.values(obj)
          .filter((c) => c.count > 0)
          .sort((a, b) => b.date - a.date)
      );
    } else {
      const getN = (b: boolean) => (b ? 1 : 0);
      setItems(
        Object.values(obj)
          .filter((c) => c.count === 0)
          .sort((a, b) => getN(b.user.disabled) - getN(a.user.disabled))
      );
    }
  }, [data, users.length, filter]);

  return items;
}

function UsersCountTable({
  data,
  filter
}: {
  data: Countable[];
  filter: number;
}) {
  const items = useUsersCount(data, filter);
  const listKhassida = useAppSelector((s) => s.khassida);
  const listAlquran = useAppSelector((s) => s.alquran);
  const listAzkaar = useAppSelector((s) => s.azkaar);
  const headCells: HeadCell<UserCount>[] = [
    {
      id: 'user',
      label: 'Membre',
      formatter: ({ user: { avatar, firstName, lastName, matricule } }) => {
        return (
          <UserFormatter
            avatar={avatar}
            matricule={matricule}
            name={`${firstName} ${lastName}`}
          />
        );
      }
    },
    {
      id: 'salatou',
      label: 'Salaatu',
      formatter: (item) => {
        return (
          <ShowCountButton
            count={item.salatou}
            data={item.dataSalatou}
            title="Limu Salaatu Yi"
            user={item.user}
            dataList={[]}
          />
        );
      }
    },
    {
      id: 'khassida',
      label: 'Xasiida',
      formatter: (item) => {
        return (
          <ShowCountButton
            count={item.khassida}
            data={item.dataKhassida}
            title="Limu  Xasiida yi"
            user={item.user}
            dataList={listKhassida}
          />
        );
      }
    },
    {
      id: 'alquran',
      label: 'Al quran',
      formatter: (item) => {
        return (
          <ShowCountButton
            count={item.alquran}
            data={item.dataAlquran}
            title="Limu al quran ji"
            user={item.user}
            dataList={listAlquran}
          />
        );
      }
    },
    {
      id: 'azkaar',
      label: 'Azkaar',
      formatter: (item) => {
        return (
          <ShowCountButton
            count={item.azkaar}
            data={item.dataAzkaar}
            title="Limu Azkaar yi"
            user={item.user}
            dataList={listAzkaar}
          />
        );
      }
    }
  ];
  if (filter === 1) {
    headCells.push({
      id: 'count',
      label: 'Bloqué?',
      formatter: ({ user }) => (
        <SwitchButton
          updateFunction={(value) => toggleUserStatus(user.uid)}
          value={!!user.disabled}
        />
      )
    });
  }
  headCells.push({
    id: 'uid',
    label: 'Action',
    formatter: (item) => {
      return (
        <>
          <ShowUserButton item={item.user} />
          <DeleteButton
            id={item.user.uid}
            deleteFunction={deleteUserFunction}
            error={'Une erreur est survenue'}
            success={'Suppression terminé avec succès'}
          />
        </>
      );
    }
  });

  return <Table keyField="uid" headCells={headCells} data={items} />;
}

export function ShowCountButton({
  data,
  title,
  count,
  user,
  dataList
}: {
  data: Countable[];
  title: string;
  count: number;
  user: UserInfos;
  dataList: (Khassida | AlQuran | Azkaar)[];
}) {
  const [show, setShow] = React.useState(false);
  const items = useMemo(() => {
    if (!show) return [];
    if (dataList.length === 0) return data;
    const newItems: CountableMemberWithCount[] = [...data];
    const listMap = new Map(dataList.map((i) => [i.uid, i]));
    for (let it of newItems) {
      it.item = listMap.get(it.itemId);
    }
    return newItems;
  }, [dataList, data, show]);
  if (count === 0) return <Box>{count}</Box>;
  return (
    <>
      {show && (
        <LimtuModal
          item={{ count: count, counts: items, uid: '', user: user }}
          onHide={() => setShow(false)}
          title={title}
          show={show}
        />
      )}
      <Tooltip title={'Voir les détails'} arrow placement="top-start">
        <Button
          onClick={() => setShow(true)}
          variant="text"
          startIcon={<RemoveRedEyeTwoTone />}
        >
          {count}
        </Button>
      </Tooltip>
    </>
  );
}
